import React from "react";
import { fireEvent, render, screen, waitFor } from "@testing-library/react";
import "@testing-library/jest-dom";
import { DashboardPanel } from "./DashboardPanel";
import { DashboardProvider } from "../DashboardContext";

const mockGetPanel = jest.fn();
jest.mock("../api", () => ({
  useOrganizationDashboardsApi: () => ({ getPanel: mockGetPanel }),
  dashboardPanelErrorFrom: () => null,
}));
jest.mock("./DrilldownDialog", () => ({
  DrilldownDialog: () => null,
}));

const panel = (panelId: string) => ({
  panel_id: panelId,
  title: panelId,
  kind: "kpi" as const,
  refresh_interval_seconds: 60,
});

describe("DashboardPanel", () => {
  beforeEach(() => mockGetPanel.mockReset());

  it("isolates a panel failure and retries only that panel", async () => {
    mockGetPanel.mockImplementation(
      (_org: string, _module: string, id: string) => {
        if (
          id === "failed" &&
          mockGetPanel.mock.calls.filter((call) => call[2] === "failed")
            .length === 1
        )
          return Promise.reject(new Error("Named provider failure"));
        return Promise.resolve({
          status: "ok",
          data: { value: id === "healthy" ? 7 : 8 },
          updated_at: "2026-10-12T10:00:00Z",
        });
      },
    );
    render(
      <DashboardProvider>
        <div>
          <DashboardPanel
            organizationId="org"
            moduleId="ida"
            level="overview"
            panel={panel("failed")}
          />
          <DashboardPanel
            organizationId="org"
            moduleId="ida"
            level="overview"
            panel={panel("healthy")}
          />
        </div>
      </DashboardProvider>,
    );
    expect(
      await screen.findByText("Could not load failed"),
    ).toBeInTheDocument();
    expect(await screen.findByText("7")).toBeInTheDocument();
    fireEvent.click(screen.getByRole("button", { name: "Retry" }));
    await waitFor(() =>
      expect(
        screen.queryByText("Could not load failed"),
      ).not.toBeInTheDocument(),
    );
    expect(screen.getByText("8")).toBeInTheDocument();
    expect(
      mockGetPanel.mock.calls.filter((call) => call[2] === "healthy"),
    ).toHaveLength(1);
  });

  it("shows API named errors and unavailable or empty states", async () => {
    mockGetPanel.mockImplementation(
      (_org: string, _module: string, id: string) =>
        Promise.resolve(
          id === "named-error"
            ? {
                status: "error",
                error: {
                  code: "provider_failed",
                  message: "Provider is not responding.",
                  correlation_id: "ref-42",
                },
              }
            : id === "missing-coverage"
              ? {
                  status: "unavailable",
                  unavailable_reason:
                    "The source does not cover the full requested time range.",
                }
              : { status: "empty", data: null },
        ),
    );
    render(
      <DashboardProvider>
        <div>
          <DashboardPanel
            organizationId="org"
            moduleId="ida"
            level="detail"
            panel={panel("named-error")}
          />
          <DashboardPanel
            organizationId="org"
            moduleId="ida"
            level="detail"
            panel={panel("missing-coverage")}
          />
          <DashboardPanel
            organizationId="org"
            moduleId="ida"
            level="detail"
            panel={panel("empty-data")}
          />
        </div>
      </DashboardProvider>,
    );
    expect(
      await screen.findByText("Provider is not responding."),
    ).toBeInTheDocument();
    expect(
      screen.getByText("provider_failed", { exact: false }),
    ).toBeInTheDocument();
    expect(
      screen.getByText(
        "The source does not cover the full requested time range.",
      ),
    ).toBeInTheDocument();
    expect(screen.getByText("No data for this period.")).toBeInTheDocument();
  });

  it("shows the drill-down action when the descriptor has a drill-down", async () => {
    mockGetPanel.mockResolvedValue({
      status: "ok",
      data: { value: 1 },
      updated_at: "2026-10-12T10:00:00Z",
    });
    render(
      <DashboardProvider>
        <DashboardPanel
          organizationId="org"
          moduleId="myvault"
          level="detail"
          panel={{
            ...panel("view-edits"),
            drilldown: {
              columns: [{ key: "action", label: "Action" }],
              page_size: 25,
              csv_export: true,
            },
          }}
        />
      </DashboardProvider>,
    );

    expect(
      await screen.findByRole("button", { name: "Expand view-edits" }),
    ).toBeInTheDocument();
  });

  it("shows how many LLM usage records lack workspace attribution", async () => {
    mockGetPanel.mockResolvedValue({
      status: "ok",
      data: {
        columns: [{ key: "calls", label: "Calls", type: "number" }],
        rows: [{ calls: 2 }],
        total_rows: 1,
        excluded_unattributed_records: 7,
      },
    });
    render(
      <DashboardProvider>
        <DashboardPanel
          organizationId="org"
          moduleId="ida"
          level="detail"
          panel={{ ...panel("llm_usage"), kind: "table" }}
        />
      </DashboardProvider>,
    );

    expect(
      await screen.findByText("7 records excluded (no workspace attribution)"),
    ).toBeInTheDocument();
  });

  it("does not show the drill-down action without a descriptor", async () => {
    mockGetPanel.mockResolvedValue({
      status: "ok",
      data: { value: 1 },
      updated_at: "2026-10-12T10:00:00Z",
    });
    render(
      <DashboardProvider>
        <DashboardPanel
          organizationId="org"
          moduleId="myvault"
          level="detail"
          panel={panel("view-edits")}
        />
      </DashboardProvider>,
    );

    await waitFor(() => expect(mockGetPanel).toHaveBeenCalled());
    expect(
      screen.queryByRole("button", { name: "Expand view-edits" }),
    ).not.toBeInTheDocument();
  });
});
